import { useLocation, useSearchParams } from 'react-router';
import { z } from 'zod';

export const parseAsBool = z.preprocess(
    (val) => String(val).toLowerCase() === 'true',
    z.boolean(),
);

/**
 * Options for {@link useParamState}.
 *
 * @template T - The expected type of the parsed parameter value.
 */
interface UseParamStateOptions<T> {
    /** Zod schema used to parse and validate the raw URL parameter string. */
    type: z.ZodType<T | null, z.ZodTypeDef, unknown>;
    /** Fallback value used when the parameter is missing or fails validation. */
    defaultValue?: T;
    /** replace URL history */
    replace?: boolean;
}

/**
 * Syncs a URL search parameter with React state, with Zod validation.
 *
 * Reads the parameter from the URL, parses it with the provided Zod schema,
 * and falls back to `options.defaultValue` or `null` if the value is missing or invalid.
 *
 * @template T - The expected type of the parsed parameter value.
 * @param name - The name of the URL search parameter (e.g. `'tab'`).
 * @param options - Zod schema and default value. See {@link UseParamStateOptions}.
 * @returns Current value and a setter that updates the URL parameter.
 *
 * @example
 * const tabSchema = z.enum(['details', 'billing', 'history']);
 *
 * const [tab, setTab] = useParamState('tab', {
 *     type: tabSchema,
 * });
 *
 * @example
 * const [page, setPage] = useParamState('page', {
 *     type: z.coerce.number().min(0).max(3),
 *     defaultValue: 0,
 * });
 */

export function useParamState<T extends string | number | boolean>(
    name: string,
    options: UseParamStateOptions<T> & { defaultValue: T },
): readonly [T, (value: T) => void];

export function useParamState<T extends string | number | boolean>(
    name: string,
    options: UseParamStateOptions<T>,
): readonly [T | null, (value: T | null) => void];

export function useParamState<T extends string | number | boolean>(
    name: string,
    options: UseParamStateOptions<T>,
) {
    const [searchParams, setSearchParams] = useSearchParams();
    const { state } = useLocation();

    const { defaultValue = null, replace = true } = options;

    const raw = searchParams.get(name);
    const value = raw
        ? options.type.catch(defaultValue).parse(raw)
        : defaultValue;

    // if we end up passing setValue down to children often, we should implement this fn as useCallback()
    const setValue = (newValue: T) => {
        setSearchParams(
            (prev) => {
                const next = new URLSearchParams(prev);
                if (newValue === null || newValue === defaultValue) {
                    next.delete(name);
                } else {
                    next.set(name, `${newValue}`);
                }
                return next;
            },
            { replace, state },
        );
    };

    return [value, setValue] as const;
}
